import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { sriAgentScheduleApi } from "./axios";
import { ValidateCurrentToken, LogoutSystem } from "../../helpers";

export type SriAgentTipo =
  | "recibidos"
  | "retenciones"
  | "emitidos"
  | "anulados"
  | "retenciones_emitidas";

export interface SriAgentScheduleI {
  id: number;
  clientId: number;
  clientName: string;
  clientDocNumber: string | null;
  tipo: SriAgentTipo;
  intervalMinutes: number;
  active: boolean;
  lastJobAt: string | null;
  lastJobStatus: string | null;
}

export interface SriAgentClientI {
  id: number;
  name: string;
  docNumber: string | null;
  listo: boolean;
}

export interface SriAgentSchedulePayloadI {
  clientId?: number;
  tipo?: SriAgentTipo;
  intervalMinutes?: number;
  active?: boolean;
}

const request = async <T>(fn: (token: string) => Promise<{ data: T }>): Promise<T> => {
  try {
    const token = ValidateCurrentToken();
    if (!token) throw new Error("TOKEN INVALIDO");
    const { data } = await fn(token);
    return data;
  } catch (err) {
    const res = (err as AxiosError<{ message?: string }>).response;
    if (res?.status === 401) LogoutSystem();
    throw new Error(res?.data?.message ?? (err as Error).message);
  }
};

const QUERY_KEY = ["sri-agent-schedules"];

export const useSriAgentSchedules = () =>
  useQuery(QUERY_KEY, () =>
    request<{ data: SriAgentScheduleI[] }>((Authorization) =>
      sriAgentScheduleApi.get("", { headers: { Authorization } }),
    ).then((r) => r.data),
  );

export const useSriAgentClients = () =>
  useQuery(["sri-agent-clients"], () =>
    request<{ data: SriAgentClientI[] }>((Authorization) =>
      sriAgentScheduleApi.get("/clients", { headers: { Authorization } }),
    ).then((r) => r.data),
  );

export const useSriAgentScheduleMutations = () => {
  const queryClient = useQueryClient();
  const onSuccess = () => queryClient.invalidateQueries(QUERY_KEY);

  const create = useMutation(
    (payload: SriAgentSchedulePayloadI) =>
      request((Authorization) =>
        sriAgentScheduleApi.post("", payload, { headers: { Authorization } }),
      ),
    { onSuccess },
  );
  const update = useMutation(
    ({ id, ...payload }: SriAgentSchedulePayloadI & { id: number }) =>
      request((Authorization) =>
        sriAgentScheduleApi.put(`/${id}`, payload, { headers: { Authorization } }),
      ),
    { onSuccess },
  );
  const remove = useMutation(
    (id: number) =>
      request((Authorization) =>
        sriAgentScheduleApi.delete(`/${id}`, { headers: { Authorization } }),
      ),
    { onSuccess },
  );

  return { create, update, remove };
};
